import type { InputHTMLAttributes, ReactNode } from 'react';

export type FieldAppearance = 'box' | 'underline';

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldProps {
  /** Visible label text (also the accessible name of the control). */
  label: string;
  /** Control name; also seeds the generated id when `id` is omitted. */
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  type?: string;
  appearance?: FieldAppearance;
  /** Help text rendered under the control and wired via aria-describedby. */
  hint?: string;
  /** Error text; sets aria-invalid and wires via aria-describedby. */
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  /** Render a `<textarea>` instead of an `<input>`. */
  multiline?: boolean;
  rows?: number;
  /** Render a `<select>` from these options. */
  options?: FieldOption[];
  /** Trailing adornment, e.g. a ".nuffsaid.cloud" suffix. */
  suffix?: ReactNode;
  id?: string;
}

export function Field({
  label,
  name,
  value,
  onChange,
  type = 'text',
  appearance = 'box',
  hint,
  error,
  required = false,
  disabled = false,
  placeholder,
  autoComplete,
  inputMode,
  multiline = false,
  rows,
  options,
  suffix,
  id,
}: FieldProps) {
  const controlId = id ?? `field-${name}`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const invalid = error !== undefined && error !== '';

  const describedBy = [hint ? hintId : null, invalid ? errorId : null].filter(Boolean).join(' ');

  const wrapperClasses = [
    'nuff-field',
    `nuff-field--${appearance}`,
    invalid && 'nuff-field--invalid',
  ]
    .filter(Boolean)
    .join(' ');

  const shared = {
    id: controlId,
    name,
    className: 'nuff-field__input',
    required,
    disabled,
    'aria-invalid': invalid,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(value !== undefined ? { value } : {}),
    ...(placeholder ? { placeholder } : {}),
  };

  let control: ReactNode;
  if (options) {
    control = (
      <select {...shared} onChange={(e) => onChange?.(e.target.value)}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  } else if (multiline) {
    control = (
      <textarea
        {...shared}
        {...(rows !== undefined ? { rows } : {})}
        onChange={(e) => onChange?.(e.target.value)}
      />
    );
  } else {
    control = (
      <input
        {...shared}
        type={type}
        {...(autoComplete ? { autoComplete } : {})}
        {...(inputMode ? { inputMode } : {})}
        onChange={(e) => onChange?.(e.target.value)}
      />
    );
  }

  return (
    <div className={wrapperClasses}>
      <label className="nuff-field__label" htmlFor={controlId}>
        {label}
        {required && (
          <>
            <span className="nuff-field__required" aria-hidden="true">
              {'*'}
            </span>
            <span className="nuff-sr-only">{' (required)'}</span>
          </>
        )}
      </label>
      {suffix ? (
        <div className="nuff-field__control-wrap">
          {control}
          <span className="nuff-field__suffix">{suffix}</span>
        </div>
      ) : (
        control
      )}
      {hint && (
        <p id={hintId} className="nuff-field__hint">
          {hint}
        </p>
      )}
      {invalid && (
        <p id={errorId} className="nuff-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
